import React, { type ReactElement, useState } from 'react'
import { Box } from '@mui/material'

import { Button, Icon, IconButton, TextField, Typography } from '~components/internal'
import LocationEntryModal from '~components/internal/LocationEntryModal'
import { typography } from '~tokens/internal'
import { type Address0, type IdentityId, formatAddress0 } from '~types'

import './AddressInputTable.scss'

type AddressInputTableProps = {
  setAddresses: React.Dispatch<React.SetStateAction<Address0[]>>
  addresses: Address0[]
  organizationId?: IdentityId
}

const AddressInputTable = ({
  setAddresses,
  addresses,
  organizationId,
}: AddressInputTableProps): ReactElement => {
  const [showAddressInput, setShowAddressInput] = useState<boolean>(true)
  const [showLocationEntry, setShowLocationEntry] = useState<boolean>(false)

  const handleAddAddress = (address: Address0): void => {
    setAddresses((prev) => [...prev, address])
    setShowAddressInput(false)
    setShowLocationEntry(false)
  }

  const handleRemoveAddress = (address: Address0): void => {
    setAddresses((prev) => prev.filter((a) => a.id !== address.id))
  }

  const handleCancelAddAddress = (): void => {
    setShowAddressInput(false)
    setShowLocationEntry(false)
  }

  return (
    <Box className='address-input-table'>
      <Typography variant='subtitle2' color='textSecondary' className='address-input-table__label'>
        Addresses
      </Typography>
      <Box className='address-input-table__list'>
        {addresses.map((address) => (
          <Box key={address.id} className='address-input-table__row'>
            <Box className='address-input-table__chip'>
              <Typography variant='body2'>{formatAddress0(address)}</Typography>
            </Box>
            <IconButton size='small' onClick={() => handleRemoveAddress(address)}>
              <Icon name='Remove' size={typography.fontSize.lg} />
            </IconButton>
          </Box>
        ))}

        {showAddressInput && (
          <Box className='address-input-table__entry'>
            {showLocationEntry && (
              <LocationEntryModal
                onSave={(address: Address0) => handleAddAddress(address)}
                onClose={handleCancelAddAddress}
                organizationId={organizationId}
                requestAddress={true}
                requestCoordinates={false}
              />
            )}
            {!showLocationEntry && (
              <TextField
                placeholder='Enter Location'
                onClick={() => setShowLocationEntry(true)}
                inputProps={{ readOnly: true, style: { cursor: 'pointer' } }}
                sx={{ cursor: 'pointer' }}
                className='address-input-table__selector'
              />
            )}
          </Box>
        )}

        {!showAddressInput && (
          <Button
            variant='text'
            size='small'
            width='fit'
            startIcon={<Icon name='AddCircleOutlined' />}
            onClick={() => setShowAddressInput(true)}
          >
            Add another
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default AddressInputTable
